<template>
	<div class="EditReportPeople">
		<!-- Add people header -->
		<div class="Page-section Page-section--primaryLight">
			<div class="Page-section-inner Page-section-inner--md">
				<!-- Heading and description -->
				<CloHeading level="2">{{ people.length > 1 ? $t('tripReport.managePeople') : $t('tripReport.addPeople') }}</CloHeading>
				<p class="u-stack-sm">
					{{ $t('tripReport.addPeopleDesc') }}
				</p>
				<!-- Invite module -->
				<section aria-labelledby="invite">
					<CloAccordion>
						<CloAccordionItem :startExpanded="true">
							<template v-slot:heading>
								<CloHeading id="invite" level="3" size="4">{{ $t('tripReport.invite') }}</CloHeading>
							</template>
							<template v-slot:content>
								<form class="EditReportPeople-invite">
									<!-- Invite input -->
									<div class="u-stack-md">
										<label class="Heading Heading--h5" for="invite-people">
											<span class="Heading-main">{{ $t('tripReport.peopleToInvite') }}</span>
											<span class="Heading-sub Heading-sub--inline">{{ $t('shareDiv.ToExplain') }}</span>
										</label>
										<textarea id="invite-people" v-model="inviteesString"></textarea>
									</div>
									<!-- Options (Suggested and My Contacts) -->
									<CloGridAuto gap="md" class="u-stack-md">
										<!-- Suggested people (based on shared checklists) -->
										<div v-if="suggestedPeople.length > 0">
											<span class="Heading Heading--h5" for="invite-suggested">
												<span class="Heading-main">{{ $t('generic.suggested') }}</span>
												<span class="Heading-sub Heading-sub--inline">{{ $t('shareDiv.ClickToAdd') }}</span>
											</span>
											<ul id="invite-suggested">
												<li v-for="suggestedPerson in suggestedPeople" :key="suggestedPerson.userId">
													<CloButton
														type="button"
														icon="plus"
														size="sm"
														class="Button--hollow u-margin-none"
														@click.native="addInvitee(suggestedPerson.loginNameOrEmail, true)"
														>{{ suggestedPerson.alias }}</CloButton
													>
												</li>
											</ul>
										</div>
										<!-- My contacts -->
										<div v-if="contacts.length > 0">
											<span class="Heading Heading--h5" for="invite-my-contacts">
												<span class="Heading-main">{{ $t('shareDiv.Contacts') }}</span>
												<span class="Heading-sub Heading-sub--inline">{{ $t('shareDiv.ClickToAdd') }}</span>
											</span>
											<ul id="invite-my-contacts" class="u-stack-sm">
												<li v-for="contact in contacts" :key="contact.userId">
													<CloButton
														type="button"
														class="Button--link"
														@click.native="addInvitee(contact.loginNameOrEmail)"
														>{{ contact.alias }}</CloButton
													>
												</li>
											</ul>
											<p class="u-text-3 u-margin-none">
												<a :href="`${baseUrl}manage/contacts`">{{ $t('index.ManageContacts') }}</a>
											</p>
										</div>
									</CloGridAuto>
									<!-- Pre-selected role select -->
									<div class="u-stack-md">
										<span class="Heading Heading--h5">
											<span class="Heading-main">{{ $t('tripReport.role') }}</span>
										</span>
										<CloInputSelect
											id="accept-role"
											:label="$t('tripReport.role')"
											:options="[
												{ value: 'participant', label: $t('tripReport.personRoleParticipant') },
												{ value: 'editor', label: $t('tripReport.personRoleEditor') },
											]"
											v-model="acceptRole"
										/>
									</div>
									<!-- Message input -->
									<div class="u-stack-md">
										<label class="Heading Heading--h5" for="invite-message">
											<span class="Heading-main">{{ $t('shareDiv.Message') }}</span>
											<span class="Heading-sub Heading-sub--inline">{{ $t('shareDiv.Optional') }}</span>
										</label>
										<textarea id="invite-message" name="message" v-model="message"></textarea>
									</div>
									<!-- Form submission buttons -->
									<div id="invite-actions">
										<CloButton type="button" :disabled="isInviteDisabled" @click.native="invitePeople">{{
											$t('tripReport.invite')
										}}</CloButton>
										<CloButton type="button" @click.native="clearForm" hollow>{{ $t('generic.Clear') }}</CloButton>
									</div>
								</form>
							</template>
						</CloAccordionItem>
					</CloAccordion>
					<CloMessage v-if="showInviteSuccess" icon="check" title="Success" class="Message--success">{{
						$t('tripReport.inviteSuccess')
					}}</CloMessage>
					<CloMessage v-if="inviteErrorMsgs.length > 0" icon="warning" title="Error" class="Message--alert">
						<div v-for="errorMsg in inviteErrorMsgs" :key="errorMsg">{{ errorMsg }}</div>
					</CloMessage>
				</section>
			</div>
		</div>
		<!-- People content -->
		<div class="Page-section">
			<div class="Page-section-inner Page-section-inner--md">
				<!-- Accepted people -->
				<section class="u-stack-lg">
					<!-- Heading -->
					<CloHeading level="3" class="is-visuallyHidden">{{ $t('tripReport.people') }}</CloHeading>
					<!-- Stat -->
					<div class="u-stack-md">
						<CloStat
							id="stat-people"
							class="StatsIcon--inline StatsIcon--highlight"
							icon="users"
							:stat="numParticipants"
							:label="numParticipants > 1 ? $t('tripReport.people') : $t('tripReport.person')"
						></CloStat>
					</div>
					<!-- List -->
					<form>
						<!-- Trip people list -->
						<ul class="EditReportPeople-list">
							<li v-for="(person, index) in sortedPeople" :key="person.tripReportPersonId" :class="person.role">
								<div class="EditReportPeople-list-item">
									<div
										class="EditReportPeople-list-item-person"
										:class="`EditReportPeople-list-item-person--${person.role}`"
									>
										<span class="Heading Heading--h4 u-margin-none">
											<Person :person="person"></Person>
										</span>
									</div>
									<div class="EditReportPeople-list-item-role" :class="`EditReportPeople-list-item-role--${person.role}`">
										<span v-if="person.role === 'owner'">{{ $t('tripReport.personRoleOwner') }}</span>
										<CloInputSelect
											v-else-if="person.role === 'participant' || person.role === 'editor'"
											:id="`select-person-${person.tripReportPersonId}`"
											:label="$t('tripReport.personRole')"
											:options="[
												{ value: 'editor', label: $t('tripReport.personRoleEditor') },
												{ value: 'participant', label: $t('tripReport.personRoleParticipant') },
											]"
											:value="person.role"
											size="sm"
											@change="changeUserRole($event, person.tripReportPersonId)"
										/>
										<span v-else-if="person.role === 'invited'">{{ $t('tripReport.personRoleInvited') }}</span>
									</div>
									<div v-if="person.role !== 'owner'" class="EditReportPeople-list-item-remove">
										<CloButton
											type="button"
											icon="delete"
											class="Button--link u-margin-none"
											@click.native="showConfirmDelete(index, person)"
										/>
									</div>
								</div>
							</li>
							<!-- Invited emails -->
							<li v-for="email in invitedEmails" :key="email" class="EditReportPeople-list-item">
								<div class="EditReportPeople-list-item-person EditReportPeople-list-item-person--email">
									<span class="Heading Heading--h4 u-margin-none">{{ email }}</span>
								</div>
								<div class="EditReportPeople-list-item-role EditReportPeople-list-item-role--invited">
									<span>{{ $t('tripReport.personRoleInvited') }}</span>
								</div>
							</li>
						</ul>
						<CloDialog v-if="openDeletePerson" @close="closeDialog">
							<template #header>
								<h3 class="Heading">{{ $t('tripReport.removePerson') }}</h3>
							</template>
							<template #body>
								<p>{{ $t('tripReport.confirmRemovePerson', [personToDelete.person.userDisplayName]) }}</p>
							</template>
							<template #footer>
								<CloButton type="button" @click.native="removePerson()">{{ $t('tripReport.removePerson') }}</CloButton>
								<CloButton type="button" hollow @click.native="closeDialog">{{ $t('generic.Cancel') }}</CloButton>
							</template>
						</CloDialog>
					</form>

					<!-- Bulk copy -->
					<div v-if="numParticipants > 1" class="EditReportPeople-bulkCopy">
						<!-- Button -->
						<CloButton type="button" hollow @click.native="show">{{ $t('tripReport.copyAllChecklistsTo') }}</CloButton>
						<!-- Modal -->
						<CloDialog v-bind="$props" v-if="open" @close="closeDialog">
							<template #header>
								<h3 class="Heading">{{ $t('tripReport.copyAllChecklistsTo') }}</h3>
							</template>
							<template #body>
								<p>{{ $t('tripReport.copyChecklistsDesc') }}</p>
								<ul>
									<li v-for="person in copyPeople" :key="person.tripReportPersonId">
										<CloInputCheckbox
											:label="person.userDisplayName"
											:id="`select-person-${person.tripReportPersonId}`"
											:name="person.userDisplayName"
											:value="person.tripReportPersonId.toString()"
											v-model="bulkSharePeopleIds"
											size="lg"
										/>
									</li>
								</ul>
								<!-- Status messages -->
								<div v-if="copyChecklistsPending" class="Message">
									<div class="Message-title">
										<CloLoadingAnimation class="LoadingAnimation--small" />
									</div>
									<div class="Message-content">{{ $t('tripReport.copyChecklistsPending') }}</div>
								</div>
								<CloMessage v-if="copyChecklistsSuccess" icon="check" title="Success" class="Message--success">{{
									$t('tripReport.copyChecklistsSuccess')
								}}</CloMessage>
								<CloMessage v-if="copyChecklistsError" icon="warning" title="Error" class="Message--alert">{{
									$t('generic.errorTryAgain')
								}}</CloMessage>
							</template>
							<template #footer>
								<CloButton
									v-if="!copyChecklistsPending"
									type="button"
									:disabled="bulkSharePeopleIds.length === 0"
									@click.native="doBulkShare"
									>{{ $t('generic.copy') }}</CloButton
								>
								<CloButton v-if="!copyChecklistsPending" type="button" hollow @click.native="closeDialog">{{
									$t('generic.Cancel')
								}}</CloButton>
								<CloButton v-else type="button" hollow @click.native="closeDialog">{{ $t('generic.dismiss') }}</CloButton>
							</template>
						</CloDialog>
					</div>
				</section>

				<!-- Invited by email -->
				<!-- <section v-if="invitedEmails" class="EditReportPeople-invitedList">
					<CloHeading level="3" size="5" minor class="u-margin-none">Invited by email</CloHeading>
					<ul>
						<li v-for="email in invitedEmails" :key="email">
							<span>{{email}}</span>
						</li>
					</ul>
				</section> -->
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloAccordion from '@/navigation/CloAccordion/CloAccordion.vue';
import CloAccordionItem from '@/navigation/CloAccordion/CloAccordionItem.vue';
import CloDialog from '@/base/CloDialog/CloDialog.vue';
import CloInputCheckbox from '@/forms/CloInputCheckbox/CloInputCheckbox.vue';
import CloInputSelect from '@/forms/CloInputSelect/CloInputSelect.vue';
import CloGridAuto from '@/grid/CloGridAuto/CloGridAuto.vue';
import CloMessage from '@/layouts/CloMessage/CloMessage.vue';
import CloStat from './CloStat.vue';
import Person from './Person.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';

export default {
	name: 'EditReportPeople',
	components: {
		CloHeading,
		CloButton,
		CloAccordion,
		CloAccordionItem,
		CloDialog,
		CloGridAuto,
		CloInputCheckbox,
		CloInputSelect,
		CloMessage,
		CloStat,
		Person,
		CloLoadingAnimation,
	},
	data() {
		return {
			csrf: window['_csrf'],
			csrf_header: window['_csrf_header'],
			baseUrl: window.baseUrl,
			tripManageApiBaseUrl: window.tripManageApiBaseUrl,
			invitedEmails: window.invitedEmails,
			suggestedPeople: [],
			contacts: [],
			inviteesString: '',
			showInviteSuccess: false,
			copyChecklistsPending: false,
			copyChecklistsSuccess: false,
			copyChecklistsError: false,
			message: '',
			bulkSharePeopleIds: [],
			open: false,
			openDeletePerson: false,
			opener: null,
			personToDelete: null,
			acceptRole: 'participant',
			inviteErrorMsgs: [],
		};
	},
	props: {
		people: {
			type: Array,
			default: [],
		},
		reportId: {
			type: Number,
			required: true,
		},
		currentUser: Object,
	},

	mounted() {
		this.getSuggestedPeople();
		this.getContacts();
	},
	methods: {
		show(e) {
			this.open = true;
			this.opener = e.target;
		},
		showConfirmDelete(index, person) {
			this.personToDelete = { index: index, person: person };
			this.openDeletePerson = true;
		},
		closeDialog(e) {
			if (this.open) {
				this.open = false;
			} else if (this.openDeletePerson) {
				this.openDeletePerson = false;
				this.personToDelete = null;
			}
			if (this.opener) {
				this.opener.focus();
			}
		},
		addInvitee(loginNameOrEmail, isSuggested) {
			if (!this.inviteesString.includes(loginNameOrEmail)) {
				let stringToAdd = loginNameOrEmail;
				if (this.inviteesString.trim().length != 0 && !this.inviteesString.trim().endsWith(',')) {
					stringToAdd = ', ' + loginNameOrEmail;
				}
				this.inviteesString = this.inviteesString.concat(stringToAdd);
				if (this.suggestedPeople.some((e) => e.loginNameOrEmail === loginNameOrEmail)) {
					isSuggested = true;
				}
			}
			if (isSuggested) {
				this.suggestedPeople = this.suggestedPeople.filter((suggested) => suggested.loginNameOrEmail !== loginNameOrEmail);
			}
		},
		invitePeople() {
			this.showInviteSuccess = false;
			this.inviteErrorMsgs = [];
			let url = `${this.tripManageApiBaseUrl}invite-people/${this.reportId}`;
			let formData = new FormData();
			formData.append('buddies', this.inviteesString);
			formData.append('msg', this.message);
			formData.append('acceptRole', this.acceptRole);
			let headers = {};
			headers[this.csrf_header] = this.csrf;
			axios
				.post(url, formData, {
					headers: headers,
					withCredentials: true,
				})
				.then((res) => {
					// todo, collapse invite section
					this.showInviteSuccess = true;
					window.location.href = window.location.href;
				})
				.catch((err) => {
					var errorMsgs = err.response.data;
					if (errorMsgs) {
						this.inviteErrorMsgs = errorMsgs;
					} else {
						this.inviteErrorMsgs.push(this.$i18n.t('generic.errorTryAgain'));
					}
				});
		},
		doBulkShare() {
			this.copyChecklistsPending = true;
			let url = `${this.tripManageApiBaseUrl}bulk-share/${this.reportId}`;
			let params = {};
			params.tripReportPersonIds = this.bulkSharePeopleIds.toString();
			let headers = {};
			headers[this.csrf_header] = this.csrf;
			axios
				.post(
					url,
					{},
					{
						params: params,
						headers: headers,
						withCredentials: true,
					}
				)
				.then((res) => {
					this.copyChecklistsPending = false;
					this.copyChecklistsSuccess = true;
					window.location.href = window.location.href;
				})
				.catch((err) => {
					this.copyChecklistsPending = false;
					this.copyChecklistsError = true;
				});
		},
		removePerson() {
			let personId = this.personToDelete.person.tripReportPersonId;
			let personIndex = this.personToDelete.index;
			let url = this.tripManageApiBaseUrl + 'delete-person/' + this.reportId + '/' + personId;
			let headers = {};
			headers[this.csrf_header] = this.csrf;
			axios
				.delete(url, {
					headers: headers,
					withCredentials: true,
				})
				.then((res) => {
					this.$delete(this.sortedPeople, personIndex);
					this.closeDialog();
				})
				.catch((err) => {
					console.log(err);
				});
		},
		changeUserRole(newRole, personId) {
			let url = this.tripManageApiBaseUrl + 'change-role/' + this.reportId + '/' + personId + '/' + newRole;
			let headers = {};
			headers[this.csrf_header] = this.csrf;
			axios
				.patch(
					url,
					{},
					{
						headers: headers,
						withCredentials: true,
					}
				)
				.then((res) => {})
				.catch((err) => {
					console.log(err);
				});
		},
		clearForm() {
			this.inviteesString = '';
			this.message = '';
			this.getSuggestedPeople();
		},
		getSuggestedPeople() {
			let url = this.tripManageApiBaseUrl + 'suggested-people/' + this.reportId;
			axios
				.get(url)
				.then((res) => {
					this.suggestedPeople = res.data;
				})
				.catch((err) => {
					console.log(err);
				});
		},
		getContacts() {
			let url = this.tripManageApiBaseUrl + 'contacts';
			axios
				.get(url)
				.then((res) => {
					this.contacts = res.data;
				})
				.catch((err) => {
					console.log(err);
				});
		},
		setInviteesString(newInvitee) {
			this.inviteesString = this.inviteesString.concat(newInvitee);
		},
	},
	computed: {
		numParticipants() {
			let participants = this.people.filter(function (person) {
				return person.role !== 'invited';
			});
			return participants.length.toString();
		},
		numInvited() {
			let numInvited = 0;
			let invited = this.people.filter(function (person) {
				return person.role === 'invited';
			});
			numInvited = numInvited + invited.length;
			if (this.invitedEmails) {
				numInvited = numInvited + invitedEmails.length;
			}
			return numInvited.toString;
		},
		hasAddedPeople() {
			return this.people.length > 1;
		},
		sortedPeople() {
			let sortedPeople = this.people;
			let roleOrders = {
				owner: 1,
				editor: 2,
				participant: 3,
				invited: 4,
			};
			sortedPeople.sort((a, b) => {
				let aOrder = roleOrders[a.role];
				let bOrder = roleOrders[b.role];
				return aOrder - bOrder;
			});
			return sortedPeople;
		},
		nonOwnerPeople() {
			return this.sortedPeople.filter((e) => e.role !== 'owner');
		},
		copyPeople() {
			return this.sortedPeople.filter(
				(person) => person.tripReportPersonId !== this.currentUser.tripReportPersonId && person.role !== 'invited'
			);
		},
		isInviteDisabled() {
			return this.inviteesString.trim() === '';
		},
	},
};
</script>

<style lang="scss">
@use "sass:color";

.EditReportPeople {
	.Message-title .Heading {
		.Heading-icon {
			margin: 0;
		}
		.Heading-main {
			@include visuallyHidden;
		}
	}
	.InputSelect {
		label {
			@include visuallyHidden;
		}
		.InputSelect-select {
			width: auto;
			max-width: 100%;
			option {
				padding-inline-end: ($space-md + $space-sm); // only works in FF
			}
		}
	}
}
.EditReportPeople-invite {
	@include text(4);
	.Heading--h5 {
		@include stack-sm;
	}
	textarea {
		width: 100%;
		max-width: 100%;
		min-height: 4rem;
	}
	ul {
		list-style: none;
		margin: 0;
	}
	#invite-suggested {
		display: flex;
		flex-wrap: wrap;
		gap: $space-sm;
		li {
			width: auto;
			margin: 0;
			line-height: 1;
		}
		.Button {
			display: flex;
			align-items: center;
			gap: $space-xs;
		}
	}
	#invite-my-contacts {
		background-color: $color-primary-1;
		max-height: 8rem;
		overflow-y: auto;
		border-radius: $radius-sm;
		border: 1px solid $color-neutral-1;
		display: flex;
		flex-direction: column;
		gap: $space-xxs;
		.Button {
			width: 100%;
			background-color: $color-white;
			border-radius: 0;
			text-align: unset;
			@include inset-sm;
			&:hover,
			&:active,
			&:focus {
				background-color: $color-primary-0;
			}
		}
	}
	#invite-actions {
		display: flex;
		gap: $space-md;
	}
	.InputSelect-select {
		min-width: min(100%, 12rem);
	}
}

.EditReportPeople-list {
	list-style: none;
	margin-left: 0;
	margin-right: 0;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: 0;
	}
	line-height: 1;
	li {
		border-top: 1px solid color.adjust($color-black, $alpha: -0.9);
		margin: 0 (-$space-md);
		width: calc(100% + (#{$space-md} * 2));
		padding: $space-sm $space-md;
		@include breakpoint(medium) {
			margin: 0;
			width: 100%;
			padding: $space-sm 0;
		}
	}
	// Adds top margin to first invited item
	> .invited {
		margin-top: $space-md;
	}
	> .invited ~ .invited {
		margin-top: 0;
	}
	.InputSelect-select {
		min-width: 7rem;
	}
}

.EditReportPeople-list-item {
	display: flex;
	align-items: center;
	gap: $space-md;
	&-person {
		flex-grow: 1;
		&--invited .Heading,
		&--email .Heading {
			font-weight: normal;
			color: $color-text-muted;
		}
	}
	.InputSelect {
		margin: 0;
	}
	&-role {
		@include text(3);
		// text-transform: uppercase;
		&--invited {
			color: $color-text-alert;
		}
		&--declined {
			color: $color-text-error;
		}
	}
}

.EditReportPeople-bulkCopy {
	margin-top: $space-md;
	ul {
		list-style: none;
		@include stack-md;
		li {
			@include inset-squish-sm;
			@include text(4, loose);
		}
	}
	.InputCheckbox {
		display: flex;
		align-items: center;
		gap: $space-sm;
		margin: 0;
	}
	.Dialog-container-footer {
		display: flex;
		gap: $space-md;
	}
	.Button {
		margin-bottom: 0;
	}
	.Message {
		.LoadingAnimation .Icon {
			margin: 0;
		}
	}
	.EditReportPeople-bulkCopy-status {
		background-color: $color-highlight;
		@include inset-sm;
		@include stack-md;
		display: flex;
		align-items: center;
		gap: $space-sm;
		.LoadingAnimation .Icon {
			margin: 0;
		}
	}
}

.EditReportPeople-invitedList {
	@include inset-md;
	background-color: $color-neutral-0;
	border-radius: $radius-sm;
	ul {
		list-style: none;
		margin: 0;
		@include text(3, loose);
		display: flex;
		flex-wrap: wrap;
		column-gap: $space-rel-sm;
		li:not(:last-of-type)::after {
			color: $color-text-muted;
			opacity: 0.5;
			content: ',';
		}
	}
}

// Set bg on all selects for FF
select {
	background-color: white;
}
</style>
